import { NgFor, NgIf } from '@angular/common';
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { Router } from '@angular/router';
import { TourInformation } from 'src/app/interfaces/tour-information';
import { Departure } from 'src/app/interfaces/departure';
import { FilterSearchItemComponent } from "../filter-search-item/filter-search-item.component";

interface CalendarDay {
  date: Date;
  dayName: string;
  dateText: string;
  tours: {tour: TourInformation, departure: Departure}[];
}

interface TimeSlot {
  hour: string; // "09:00", "10:00", etc.
  displayTime: string; // "kl. 09:00"
  days: DayWithTours[];
}

interface DayWithTours {
  date: Date;
  dayName: string;
  dateText: string;
  tours: {tour: TourInformation, departure: Departure}[];
}

@Component({
  selector: 'app-filter-items-display',
  templateUrl: './filter-items-display.component.html',
  styleUrls: ['./filter-items-display.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    NgIf,
    NgFor,
    FilterSearchItemComponent,
    FormsModule,
    ReactiveFormsModule
]
})
export class FilterItemsDisplayComponent {

  // Inputs
  @Input() public itemsData: TourInformation[] = []
  @Input() public pageSize: number = 12

  // Outputs
  @Output() public tourSelected = new EventEmitter<TourInformation>();
  @Output() public loadMoreWeeks = new EventEmitter<void>();

  // View mode
  public viewMode: 'list' | 'calendar' = 'list';
  private readonly VIEW_MODE_STORAGE_KEY = 'filter_display_view_mode';

  // Calendar properties
  public currentWeek: Date = new Date();
  public currentWeekDays: CalendarDay[] = [];
  public timeSlots: TimeSlot[] = [];
  public currentWeekText: string = '';
  public weeksLoaded: number = 1; // Show only one week at a time

  // Pagination
  public paginationPageIndex: number = 1
  public paginationTotalPages: number = 1
  public paginationLength: number = 0
  public paginatedItemData: TourInformation[] = []

  // Sorting formgroup
  public sortingForm = new UntypedFormGroup({
    sortBy: new UntypedFormControl('')
  })

  // Sorting options
  public sortingOptions: any = []

  constructor(private router: Router) {}
  
  ngOnInit() {
    // Load saved view mode from localStorage
    this.loadSavedViewMode();

    // Load sorting options with translations
    this.sortingOptions = [
      { key: 'price-low-to-high', label: 'Lav til høj pris'},
      { key: 'price-high-to-low', label: 'Høj til lav pris'},
      { key: 'score-high-to-low', label: 'Høj til lav score'}
    ]

    // Initial pagination
    this.paginateResults(0)

    // Initialize calendar view
    this.initializeCalendarView();

    // Subscribe to sorting form changes
    this.sortingForm.valueChanges.subscribe(() => {
      this.sortResults(this.sortingForm.controls.sortBy.value as string)
      this.paginateResults(0)
      if (this.viewMode === 'calendar') {
        this.updateCalendarView();
      }
    })
  }

  ngOnChanges() {
    this.sortResults(this.sortingForm.controls.sortBy.value as string)
    this.paginateResults(0)
    if (this.viewMode === 'calendar') {
      this.updateCalendarView();
    }
  }

  /**
   * Handle view mode change
   * Author: Cursor
   */
  	public onViewModeChange(mode: 'list' | 'calendar') {
		this.viewMode = mode;
		this.saveViewMode(mode);
		if (this.viewMode === 'calendar') {
      this.updateCalendarView();
    }
  }

  /**
   * Handle tour click in calendar view
   * Author: Cursor
   */
  public onTourClick(tour: TourInformation, departure?: any) {
    const navigationExtras: any = {};
    
    // If departure data is provided, add it as query parameters
    if (departure) {
      navigationExtras.queryParams = {
        date: departure.isoDateTime.split('T')[0], // YYYY-MM-DD format
        time: departure.time,
        departureId: departure.id
      };
    }
    
    // Navigate to tour booking page with optional query parameters
    this.router.navigate(['/tour-consultant/booking', tour.id], navigationExtras);
    // Also emit event for parent component if needed
    this.tourSelected.emit(tour);
  }

  /**
   * Initialize calendar view
   */
  private initializeCalendarView() {
    this.currentWeek = this.getStartOfWeek(new Date());
    this.updateCalendarView();
  }

  /**
   * Update calendar view with current data
   */
  private updateCalendarView() {
    this.currentWeekDays = this.generateWeekDays();
    this.timeSlots = this.generateTimeSlots();
    this.currentWeekText = this.generateWeekText();
  }

  /**
   * Generate days for current week view (multiple weeks)
   */
  private generateWeekDays(): CalendarDay[] {
    const days: CalendarDay[] = [];
    const startDate = new Date(this.currentWeek);
    
    // Generate days for multiple weeks
    for (let week = 0; week < this.weeksLoaded; week++) {
      for (let day = 0; day < 7; day++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + (week * 7) + day);
        
        const calendarDay: CalendarDay = {
          date: currentDate,
          dayName: this.getDayName(currentDate),
          dateText: this.formatDate(currentDate),
          tours: this.getToursForDate(currentDate)
        };
        
        days.push(calendarDay);
      }
    }
    
    return days;
  }

  /**
   * Generate time slots with 7 day columns for each time
   * Author: Cursor
   */
  private generateTimeSlots(): TimeSlot[] {
    const timeSlotMap = new Map<string, TimeSlot>();
    
    // First, collect all unique times from all tours using departure.time
    const allTimes = new Set<string>();
    this.currentWeekDays.forEach(day => {
      day.tours.forEach(tourDeparture => {
        // Use the departure.time property directly for consistency
        const timeKey = tourDeparture.departure.time;
        allTimes.add(timeKey);
      });
    });
    
    // Create time slots with empty day structure
    allTimes.forEach(timeKey => {
      const displayTime = `kl. ${timeKey}`;
      const days: DayWithTours[] = this.currentWeekDays.map(day => ({
        date: day.date,
        dayName: day.dayName,
        dateText: day.dateText,
        tours: []
      }));
      
      timeSlotMap.set(timeKey, {
        hour: timeKey,
        displayTime: displayTime,
        days: days
      });
    });
    
    // Fill in the tours for each day/time combination
    this.currentWeekDays.forEach((day, dayIndex) => {
      day.tours.forEach(tourDeparture => {
        // Use departure.time directly for consistency
        const timeKey = tourDeparture.departure.time;
        
        const timeSlot = timeSlotMap.get(timeKey);
        if (timeSlot) {
          timeSlot.days[dayIndex].tours.push({
            tour: tourDeparture.tour,
            departure: tourDeparture.departure
          });
        }
      });
    });
    
    // Convert map to array and sort by time
    const sortedTimeSlots = Array.from(timeSlotMap.values()).sort((a, b) => {
      const [hourA, minuteA] = a.hour.split(':').map(Number);
      const [hourB, minuteB] = b.hour.split(':').map(Number);
      return (hourA * 60 + minuteA) - (hourB * 60 + minuteB);
    });
    
    return sortedTimeSlots;
  }

  	/**
	 * Get tours for a specific date
	 * Author: Cursor
	 */
	private getToursForDate(date: Date): {tour: TourInformation, departure: Departure}[] {
		const tours: {tour: TourInformation, departure: Departure}[] = [];
		const dateString = date.toISOString().split('T')[0]; // YYYY-MM-DD format
		
		this.itemsData.forEach(tour => {
			// Check availableDepartures first (filtered departures), then fall back to all departures
			const departuresToCheck = tour.availableDepartures?.length > 0 ? tour.availableDepartures : tour.departures;
			
			departuresToCheck.forEach(departure => {
				const departureDate = new Date(departure.isoDateTime);
				const departureDateString = departureDate.toISOString().split('T')[0];
				
				// Only show departures that match the date AND have available seats
				if (departureDateString === dateString && departure.qty > 0) {
					tours.push({ tour, departure });
				}
			});
		});
		
		// Sort tours by departure time (using isoDateTime for precise sorting)
		tours.sort((a, b) => {
			const dateTimeA = new Date(a.departure.isoDateTime);
			const dateTimeB = new Date(b.departure.isoDateTime);
			return dateTimeA.getTime() - dateTimeB.getTime();
		});
		
		return tours;
	}

  /**
   * Navigate to previous week
   */
  public previousWeek() {
    this.currentWeek.setDate(this.currentWeek.getDate() - 7);
    this.updateCalendarView();
  }

  /**
   * Navigate to next week
   */
  public nextWeek() {
    this.currentWeek.setDate(this.currentWeek.getDate() + 7);
    this.updateCalendarView();
  }

  /**
   * Load more weeks in calendar view
   */
  public loadNextWeek() {
    // In week view, this navigates to next week instead of loading more
    this.nextWeek();
    this.loadMoreWeeks.emit();
  }

  /**
   * Get start of week (Monday)
   */
  private getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  }

  /**
   * Get day name in Danish
   */
  private getDayName(date: Date): string {
    const days = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
    return days[date.getDay()];
  }

  /**
   * Format date for display
   */
  private formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}`;
  }

  /**
   * Generate week text for header
   */
  private generateWeekText(): string {
    const startDate = new Date(this.currentWeek);
    const endDate = new Date(this.currentWeek);
    endDate.setDate(startDate.getDate() + 6); // One week = 7 days
    
    const startText = this.formatDateLong(startDate);
    const endText = this.formatDateLong(endDate);
    
    return `${startText} - ${endText}`;
  }

  /**
   * Format date in long format
   */
  private formatDateLong(date: Date): string {
    const months = [
      'januar', 'februar', 'marts', 'april', 'maj', 'juni',
      'juli', 'august', 'september', 'oktober', 'november', 'december'
    ];
    
    return `${date.getDate()}. ${months[date.getMonth()]} ${date.getFullYear()}`;
  }

  /**
   * Load next page of tours
   * @returns void
   **/
  public loadNextPage() {
    this.paginateResults(this.paginationPageIndex + 1)
  }

  /**
   * Paginate results
   * @param pageNumber 
   */
  private paginateResults(pageNumber: number) {
    this.paginationPageIndex = pageNumber
    this.paginationTotalPages = Math.floor(this.itemsData.length / this.pageSize)
    this.paginationLength = this.itemsData.length
    this.paginatedItemData = this.itemsData.slice(0, (this.paginationPageIndex * this.pageSize) + this.pageSize)
  }

  /**
   * Sort results
   * @param sortBy
   **/
  private sortResults(sortBy: string) {
    switch (sortBy) {
      case 'price-low-to-high':
        this.itemsData.sort((a, b) => a.price - b.price)
        break;
      case 'price-high-to-low':
        this.itemsData.sort((a, b) => b.price - a.price)
        break;
      case 'score-high-to-low':
        this.itemsData.sort((a, b) => b.score - a.score)
        break;
      default:
        break;
    }
  }

  /**
   * Load saved view mode from localStorage
   * Author: Cursor
   */
  private loadSavedViewMode() {
    try {
      const savedViewMode = localStorage.getItem(this.VIEW_MODE_STORAGE_KEY);
      if (savedViewMode && (savedViewMode === 'list' || savedViewMode === 'calendar')) {
        this.viewMode = savedViewMode as 'list' | 'calendar';
      }
    } catch (error) {
      // If localStorage is not available (e.g., in some mobile environments), 
      // just use the default value
      console.log('Could not access localStorage for view mode');
    }
  }

  /**
   * Save view mode to localStorage
   * Author: Cursor
   */
  private saveViewMode(mode: 'list' | 'calendar') {
    try {
      localStorage.setItem(this.VIEW_MODE_STORAGE_KEY, mode);
    } catch (error) {
      // If localStorage is not available, just ignore the error
      console.log('Could not save view mode to localStorage');
    }
  }
}
